import {
  downloadPriorAuthAttachment as sdkDownloadPriorAuthAttachment,
  getPriorAuthDetails,
  listPatientPriorAuths,
  prescriptionDecision,
  priorAuthAction,
} from '@lotushealth/lotushealth_js_sdk/endpoints';
import { lotusHealthZod } from '@lotushealth/lotushealth_js_sdk';
import type {
  MedicationDecisionRequestT,
  MedicationDecisionResponseT,
  PriorAuthActionBody,
  PriorAuthCommandResponseT,
  PriorAuthCaseDetailsT,
  PriorAuthSummaryT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { createLotusRequestConfig } from '@/shared/api/lotus';

export async function fetchPatientPriorAuths(
  patientUserId: string,
  signal?: AbortSignal
): Promise<PriorAuthSummaryT[]> {
  const config = await createLotusRequestConfig({ signal });
  const response = await listPatientPriorAuths(patientUserId, config);
  return lotusHealthZod.listPatientPriorAuthsResponse.parse(response.data) as PriorAuthSummaryT[];
}

export async function fetchPriorAuthDetails(
  recordId: string,
  signal?: AbortSignal
): Promise<PriorAuthCaseDetailsT> {
  const config = await createLotusRequestConfig({ signal });
  const response = await getPriorAuthDetails(recordId, config);
  return lotusHealthZod.getPriorAuthDetailsResponse.parse(response.data) as PriorAuthCaseDetailsT;
}

export async function submitPriorAuthAction(
  recordId: string,
  request: PriorAuthActionBody
): Promise<PriorAuthCommandResponseT> {
  const config = await createLotusRequestConfig();
  const response = await priorAuthAction(recordId, request, config);
  return lotusHealthZod.priorAuthActionResponse.parse(response.data) as PriorAuthCommandResponseT;
}

export async function submitMedicationDecision(
  request: MedicationDecisionRequestT
): Promise<MedicationDecisionResponseT> {
  const config = await createLotusRequestConfig();
  const response = await prescriptionDecision(request, config);
  return lotusHealthZod.prescriptionDecisionResponse.parse(
    response.data
  ) as MedicationDecisionResponseT;
}

/**
 * Stream a PA attachment back from cortex (which proxies DoseSpot) as a
 * ``Blob``. The caller is responsible for handing the blob to the
 * browser as a download (see ``triggerBrowserDownload`` in the wizard);
 * this layer just owns the auth + transport and stays free of DOM
 * concerns so it can be reused from non-UI callers later.
 *
 * The SDK function returns ``AxiosResponse<unknown>`` because the
 * endpoint is documented as ``application/octet-stream`` and we don't
 * want the generator to assume a JSON body. We override
 * ``responseType: 'blob'`` so axios hands us raw bytes instead of
 * trying to parse them as text or JSON.
 */
export async function downloadPriorAuthAttachment(
  recordId: string,
  attachmentId: number,
  signal?: AbortSignal
): Promise<Blob> {
  const config = await createLotusRequestConfig({ signal });
  const response = await sdkDownloadPriorAuthAttachment(recordId, attachmentId, {
    ...config,
    responseType: 'blob',
  });
  return response.data as Blob;
}
